import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

const TermsOfService = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service | Florida's Top Site Service & Demolition</title>
        <meta name="description" content="Our terms of service outline the rules, guidelines, and obligations when using our website and services." />
        <meta name="keywords" content="terms of service, terms and conditions, legal terms, demolition services, site clearing" />
        <link rel="canonical" href="https://www.floridastopsite.com/terms-of-service" />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Terms of Service | Florida's Top Site Service & Demolition" />
        <meta property="og:description" content="Our terms of service outline the rules, guidelines, and obligations when using our website and services." />
        <meta property="og:url" content="https://www.floridastopsite.com/terms-of-service" />
        <meta property="og:image" content="https://www.floridastopsite.com/images/og-image.jpg" />
      </Helmet>
      
      <HeroSection>
        <div className="container">
          <h1>Terms of Service</h1>
        </div>
      </HeroSection>
      
      <TermsSection>
        <div className="container">
          <div className="terms-content">
            <section>
              <h2>Introduction</h2>
              <p>Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              <p>Welcome to Florida's Top Site Service & Demolition. These Terms of Service govern your use of our website and services. By accessing or using our website, you agree to be bound by these Terms. If you disagree with any part of the terms, then you may not access our services.</p>
            </section>
            
            <section>
              <h2>Use of Our Services</h2>
              <p>Our website provides information about our demolition and site clearing services. By using our website, you agree to:</p>
              <ul>
                <li>Use our website and services only for lawful purposes and in accordance with these Terms</li>
                <li>Not use our website in any way that violates any applicable local, state, national, or international law or regulation</li>
                <li>Not attempt to gain unauthorized access to any portion of the website or any systems or networks connected to the website</li>
                <li>Not engage in any activity that interferes with or disrupts the website or servers and networks connected to the website</li>
              </ul>
            </section>
            
            <section>
              <h2>Service Description</h2>
              <p>Florida's Top Site Service & Demolition offers various demolition and site clearing services, including but not limited to:</p>
              <ul>
                <li>Residential & Commercial Demolition</li>
                <li>Concrete Removal</li>
                <li>Interior Demolition</li>
                <li>Site Clearing</li>
                <li>Land Clearing</li>
                <li>Tree Removal</li>
                <li>Debris Removal</li>
              </ul>
              <p>While we strive to provide accurate and comprehensive information about our services on our website, we reserve the right to modify, suspend, or discontinue any aspect of our services at any time without prior notice.</p>
            </section>
            
            <section>
              <h2>Intellectual Property</h2>
              <p>The content on our website, including text, graphics, logos, button icons, images, audio clips, digital downloads, data compilations, and software, is the property of Florida's Top Site Service & Demolition or its content suppliers and is protected by United States and international copyright laws.</p>
              <p>You may not modify, publish, transmit, participate in the transfer or sale of, create derivative works from, distribute, display, reproduce or perform, or in any way exploit in any format whatsoever any of the website content, in whole or in part, without our prior written consent.</p>
            </section>
            
            <section>
              <h2>User Accounts</h2>
              <p>Some features of our website may require you to create an account. You are responsible for maintaining the confidentiality of your account and password and for restricting access to your computer. You agree to accept responsibility for all activities that occur under your account or password.</p>
              <p>You must provide accurate, current, and complete information when creating an account. If you provide any information that is untrue, inaccurate, not current, or incomplete, we have the right to suspend or terminate your account and refuse any and all current or future use of our services.</p>
            </section>
            
            <section>
              <h2>Service Quotes and Estimates</h2>
              <p>Any quotes or estimates provided through our website are subject to the following terms:</p>
              <ul>
                <li>Quotes are estimates based on the information provided and may be subject to change after an on-site inspection</li>
                <li>Quotes are valid for 30 days from the date of issuance, unless otherwise stated</li>
                <li>Final pricing may vary based on the actual scope of work, unforeseen circumstances, or changes to the project requirements</li>
                <li>All quotes are subject to our standard contract terms and conditions</li>
              </ul>
            </section>
            
            <section>
              <h2>Payment Terms</h2>
              <p>Payment terms for our services are as follows:</p>
              <ul>
                <li>A deposit may be required before commencing work</li>
                <li>Final payment is due upon completion of the project, unless otherwise specified in the contract</li>
                <li>We accept various payment methods, including credit cards, checks, and bank transfers</li>
                <li>Late payments may be subject to additional fees as specified in the service contract</li>
              </ul>
              <p>For detailed payment terms specific to your project, please refer to your service contract or contact our office.</p>
            </section>
            
            <section>
              <h2>Liability Limitations</h2>
              <p>Florida's Top Site Service & Demolition will not be liable for any damages of any kind arising from the use of our website or services, including, but not limited to, direct, indirect, incidental, punitive, and consequential damages.</p>
              <p>To the maximum extent permitted by applicable law, Florida's Top Site Service & Demolition assumes no liability or responsibility for any:</p>
              <ul>
                <li>Errors or omissions in the content of our website</li>
                <li>Personal injury or property damage, of any nature whatsoever, resulting from your access to and use of our website or services</li>
                <li>Unauthorized access to or use of our secure servers and/or any personal information stored therein</li>
                <li>Interruption or cessation of transmission to or from our website</li>
                <li>Bugs, viruses, trojan horses, or the like that may be transmitted to or through our website</li>
              </ul>
            </section>
            
            <section>
              <h2>Indemnification</h2>
              <p>You agree to indemnify, defend, and hold harmless Florida's Top Site Service & Demolition, its officers, directors, employees, agents, licensors, and suppliers from and against all losses, expenses, damages and costs, including reasonable attorneys' fees, resulting from any violation of these Terms or any activity related to your account (including negligent or wrongful conduct) by you or any other person accessing our website using your account.</p>
            </section>
            
            <section>
              <h2>Third-Party Links</h2>
              <p>Our website may contain links to third-party websites or services that are not owned or controlled by Florida's Top Site Service & Demolition.</p>
              <p>Florida's Top Site Service & Demolition has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third-party websites or services. We do not warrant the offerings of any of these entities/individuals or their websites.</p>
              <p>You acknowledge and agree that Florida's Top Site Service & Demolition shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods, or services available on or through any such third-party websites or services.</p>
            </section>
            
            <section>
              <h2>Termination</h2>
              <p>We may terminate or suspend your access to our website and services immediately, without prior notice or liability, for any reason whatsoever, including, without limitation, if you breach these Terms.</p>
              <p>All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.</p>
            </section>
            
            <section>
              <h2>Governing Law</h2>
              <p>These Terms shall be governed and construed in accordance with the laws of the State of Florida, United States, without regard to its conflict of law provisions.</p>
              <p>Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect.</p>
            </section>
            
            <section>
              <h2>Changes to Terms</h2>
              <p>We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.</p>
              <p>By continuing to access or use our website after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, please stop using our website and services.</p>
            </section>
            
            <section>
              <h2>Contact Us</h2>
              <p>If you have any questions about these Terms, please contact us:</p>
              <ul>
                <li>By email: FLSITESERVICE@GMAIL.COM</li>
                <li>By phone: (386) 872-2144 or (512) 767-4123</li>
                <li>By mail: Crescent City, FL 32112</li>
              </ul>
            </section>
          </div>
        </div>
      </TermsSection>
    </>
  );
};

// Styled Components
const HeroSection = styled.div`
  background: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('/images/hero-bg.jpg') center/cover no-repeat;
  height: 30vh;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at center, rgba(255, 140, 0, 0.2) 0%, rgba(0, 0, 0, 0) 70%);
  }
  
  .container {
    position: relative;
    z-index: 2;
  }
  
  h1 {
    color: white;
    font-size: 4rem;
    font-weight: 700;
    margin: 0;
    text-shadow: 0 2px 5px rgba(0, 0, 0, 0.5);
    
    @media (max-width: 768px) {
      font-size: 3rem;
    }
  }
`;

const TermsSection = styled.section`
  padding: 6rem 0;
  background-color: var(--background-color);
  
  .container {
    max-width: 900px;
    margin: 0 auto;
    padding: 0 2rem;
    display: block;
  }
  
  .terms-content {
    background-color: #f5f5f5;
    border-radius: 10px;
    padding: 3rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
    display: block;
    overflow: visible;
    
    @media (max-width: 768px) {
      padding: 2rem;
    }
    
    section {
      margin-bottom: 4rem;
      display: block;
      opacity: 1 !important;
      visibility: visible !important;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
    
    h2 {
      color: #ff8c00;
      font-size: 2.4rem;
      font-weight: 700;
      margin-bottom: 1.5rem;
      padding-bottom: 1rem;
      border-bottom: 2px solid #ff8c00;
      display: block;
      
      @media (max-width: 768px) {
        font-size: 2rem;
      }
    }
    
    h3 {
      color: #333333;
      font-size: 1.8rem;
      font-weight: 600;
      margin: 2rem 0 1rem;
      display: block;
      
      @media (max-width: 768px) {
        font-size: 1.6rem;
      }
    }
    
    p {
      color: #333333;
      font-size: 1.6rem;
      line-height: 1.7;
      margin-bottom: 1.5rem;
      display: block;
      
      @media (max-width: 768px) {
        font-size: 1.5rem;
      }
    }
    
    ul {
      color: #333333;
      padding-left: 2rem;
      margin-bottom: 1.5rem;
      display: block;
      
      li {
        font-size: 1.6rem;
        line-height: 1.7;
        margin-bottom: 0.8rem;
        display: list-item;
        
        @media (max-width: 768px) {
          font-size: 1.5rem;
        }
      }
      
      strong {
        color: #e67e00;
        font-weight: 700;
      }
    }
    
    a {
      color: #ff8c00;
      text-decoration: none;
      font-weight: 600;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

export default TermsOfService; 